/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import MessageType from './MessageType';
import DateSelector from './DateSelector';
import FilterSelector from './FilterSelector';
import EmailSearchField from './EmailSearchField';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import LogCard from 'shared/components/EmailLogs/LogCard';
import { subMonths } from 'date-fns';
import { Checkbox } from '@mui/material';
import { useClient } from 'shared/apollo';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { EventLogs, toRenderLog } from './utils';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  getSesLogByEmail,
  getDonorsFilters,
  Donors,
  getNotificationDeliveriesByFilters,
  getAllNotificationDeliveries,
  NotificationDelivery,
  getPortfolios,
  getPortfoliosById,
  Portfolios,
  getAccounts,
  Accounts,
  getReports,
} from './api';
import { SESEventLog } from './eventUtils';
import { MEDIA_BREAKPOINTS } from 'shared/design-system/settings/variables/Breakpoints';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { gql, ApolloClient, ApolloQueryResult } from 'apollo-boost';
import { getPrograms } from 'npo/pages/ReportsBeta/NewReport/api';
import { Program } from 'npo/pages/Reports/NewReportDialog';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'npo/pages/ReportBuilder/styles';

type MaterialUiPickersDate = Date | null;

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  background-color: #f9f9f9;
  padding: ${SPACERS.extraLarge} ${SPACERS.small};
  gap: ${SPACERS.extraLarge};
  .search-field {
    .search-field {
      margin-bottom: 0;
      border-color: ${COLORS.grayLight};
      border-radius: ${SPACERS.extraTiny};
      background-color: ${COLORS.whiteBase};
    }
  }
  .filter_dropdown__wrapper .cta_clear {
    background-color: ${COLORS.grayLight};
  }
  .left-grid {
    width: 343px;
  }
  .right-grid {
    flex: 1;
  }
  .date-selector {
    margin-bottom: ${SPACERS.small};
  }
  ${MEDIA_BREAKPOINTS.smallerThanLarge} {
    display: block;
    .left-grid {
      width: 100%;
    }
  }
`;

const SESLogs: React.FC = () => {
  const client = useClient();
  const [type, setType] = useState<SESEventLog['eventType'] | undefined>(
    undefined,
  );
  const [date, setDate] = useState<MaterialUiPickersDate>();
  const [logs, setLog] = useState<EventLogs>({ events: {}, messageIds: [] });
  const [searchEmail, setSearchEmail] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [
    portfoliosList,
    makePortfoliosListRequest,
    setPortfoliosList,
  ] = useGraphQLRequest<{
    adminPortfolios: Portfolios[];
  }>();
  const [
    accountsList,
    makeAccountsListRequest,
    setAccountsList,
  ] = useGraphQLRequest<{
    adminAccounts: Accounts[];
  }>();
  const [
    programsList,
    makeProgramsListRequest,
    setProgramsList,
  ] = useGraphQLRequest<{
    allPrograms: Program[];
  }>();
  const [donorsList, makeDonorsListRequest, setDonorsList] = useGraphQLRequest<{
    adminAccounts: Donors[];
  }>();
  const [
    allNotificationDeliveriesList,
    makeAllNotificationDeliveriesListRequest,
  ] = useGraphQLRequest<{
    allNotificationDeliveries: NotificationDelivery[];
  }>();
  const [selectedPortfolios, setSelectedPortfolios] = useState<number[]>([]);
  const [selectedNPO, setSelectedNPO] = useState<number[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
  const [selectedDonors, setSelectedDonors] = useState<number[]>([]);
  const [programNotificationIds, setProgramNotificationIds] = useState<
    string[]
  >([]);
  const [portfolioNotificationIds, setPortfolioNotificationIds] = useState<
    string[]
  >([]);
  const [donorNotificationIds, setDonorNotificationIds] = useState<string[]>(
    [],
  );
  const [NPONotificationIds, setNPONotificationIds] = useState<string[]>([]);
  const [investmentNotificationIds, setInvestmentNotificationIds] = useState<
    string[]
  >([]);
  const [investmentIdInput, setInvestmentIdInput] = useState<string>();
  const [selectedInvestment, setSelectedInvestment] = useState<number>();
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [latest, setLatest] = useState<boolean>(false);
  const [anyFiltersApplied, setAnyFiltersApplied] = useState(false);
  const requestIdRef = useRef(0);
  const PER_PAGE = 10;

  const today = new Date().setHours(0, 0, 0, 0);

  const [recencyDate, setRecencyDate] = useState<MaterialUiPickersDate>(null);
  const handleRecencyDate = (months: number) =>
    setRecencyDate(subMonths(today, months));
  const MONTHS: Record<string, () => void> = {
    'No Time Constraint': () => setRecencyDate(null),
    'Last 6 Months': () => handleRecencyDate(6),
    'Last 12 Months': () => handleRecencyDate(12),
    'Last 18 Months': () => handleRecencyDate(18),
    'Last 24 Months': () => handleRecencyDate(24),
  };
  useEffect(() => {
    findNotificationsForProgramIds(client!, selectedPrograms);
  }, [selectedPrograms]);

  useEffect(() => {
    findNotificationsForPortfolioIds(client!, selectedPortfolios);
  }, [selectedPortfolios]);

  useEffect(() => {
    findNotificationsForNPOIds(client!, selectedNPO);
  }, [selectedNPO]);

  useEffect(() => {
    findNotificationsForDonorIds(client!, selectedDonors);
  }, [selectedDonors]);

  useEffect(() => {
    if (!selectedInvestment) return;
    findNotificationsForInvestmentId(client!, selectedInvestment);
  }, [selectedInvestment]);

  useEffect(() => {
    findLogs();
  }, [
    searchEmail,
    date,
    type,
    recencyDate,
    programNotificationIds,
    portfolioNotificationIds,
    donorNotificationIds,
    NPONotificationIds,
    investmentNotificationIds,
  ]);

  useEffect(() => {
    if (
      date ||
      recencyDate ||
      type ||
      selectedPrograms.length ||
      selectedPortfolios.length ||
      selectedNPO.length ||
      selectedDonors.length ||
      selectedInvestment
    ) {
      setAnyFiltersApplied(true);
    } else {
      setAnyFiltersApplied(false);
    }
  }, [
    date,
    recencyDate,
    type,
    selectedPrograms,
    selectedPortfolios,
    selectedNPO,
    selectedDonors,
    selectedInvestment,
  ]);

  const relevant = (id: number) => {
    // Ignore stale requests without clearing shown logs so results don't vanish.
    return id === requestIdRef.current;
  };

  const findLogs = async () => {
    setLoading(true);
    const currentRequestId = ++requestIdRef.current;
    const startDate = date
      ? date?.toISOString().split('T')[0]
      : recencyDate
      ? recencyDate?.toISOString().split('T')[0]
      : undefined;
    const endDate = date
      ? new Date(date)
      : recencyDate
      ? new Date()
      : undefined;
    date && endDate?.setDate(date.getDate() + 1);
    const newEndDate = endDate?.toISOString().split('T')[0];
    if (
      programNotificationIds.length ||
      portfolioNotificationIds.length ||
      selectedInvestment ||
      donorNotificationIds.length ||
      NPONotificationIds.length ||
      investmentNotificationIds.length
    ) {
      const joinedNotificationIds = [
        ...new Set([
          ...programNotificationIds,
          ...portfolioNotificationIds,
          ...donorNotificationIds,
          ...NPONotificationIds,
          ...investmentNotificationIds,
        ]),
      ];
      if (!relevant(currentRequestId)) return;
      const promises: Promise<ApolloQueryResult<any>>[] =
        joinedNotificationIds?.map(id =>
          getSesLogByEmail(
            client!,
            searchEmail || undefined,
            startDate || undefined,
            newEndDate || undefined,
            type || undefined,
            id.toString(),
          ),
        ) || [];
      const result = await Promise.all(promises);
      const flattened = result.flatMap(r =>
        r.data.allSesLogs.edges.map((e: any) => e.node),
      );
      if (!relevant(currentRequestId)) return;
      setLog(toRenderLog(flattened));
    } else {
      if (searchEmail === '' && !startDate && !newEndDate && !type) {
        setLoading(false);
        setLog({ events: {}, messageIds: [] });
        return;
      }
      const sesLogs = await getSesLogByEmail(
        client!,
        searchEmail || undefined,
        startDate || undefined,
        newEndDate || undefined,
        type || undefined,
      );
      const flattened = sesLogs.data.allSesLogs.edges.map((e: any) => e.node);
      if (!relevant(currentRequestId)) return;
      setLog(toRenderLog(flattened));
    }
    setLoading(false);
  };

  const findNotificationsForProgramIds = async (
    client: ApolloClient<object>,
    programIdsArray: number[],
  ) => {
    setLoading(true);
    const reportPromises = Array.from(programIdsArray).map(
      async (programId: number) => {
        return getReports(client!, programId);
      },
    );
    const reportResults = await Promise.all(reportPromises);
    const reportIds = reportResults.flatMap(result =>
      result.data.allReports.edges.map((e: any) => e.node.reportId),
    );
    let programnotificaiton: any = [];
    if (!allNotificationDeliveriesList.data) {
      const notificationPromises = reportIds.map(async (reportId: number) => {
        return getNotificationDeliveriesByFilters(
          client!,
          undefined,
          undefined,
          undefined,
          undefined,
          reportId,
        );
      });
      const notificationResults = await Promise.all(notificationPromises);
      programnotificaiton = notificationResults.flatMap(result =>
        result.data.allNotificationDeliveries.edges.map(
          (e: any) => e.node.sesMessageId,
        ),
      );
    } else {
      programnotificaiton = allNotificationDeliveriesList.data.allNotificationDeliveries
        .filter((e: any) => reportIds.includes(e.reportId))
        .map((e: any) => e.sesMessageId);
    }
    setProgramNotificationIds([...programnotificaiton.filter(Boolean)]);
  };

  const findNotificationsForPortfolioIds = async (
    client: ApolloClient<object>,
    portfolioIdsArray: number[],
  ) => {
    setLoading(true);
    const reportPromises = Array.from(portfolioIdsArray).map(
      async (portfolioId: number) => {
        return getPortfoliosById(client!, portfolioId);
      },
    );
    const reportResults = await Promise.all(reportPromises);
    const reportIds = reportResults.flatMap(result =>
      result.data.adminPortfolios.edges.flatMap((e: any) =>
        e.node.investments.edges.flatMap((e: any) =>
          e.node.allocations.edges.flatMap((e: any) => e.node.report.dbId),
        ),
      ),
    );
    let portfolionotificaiton: any = [];
    if (!allNotificationDeliveriesList.data) {
      const notificationPromises = reportIds.map(async (reportId: number) => {
        return getNotificationDeliveriesByFilters(
          client!,
          undefined,
          undefined,
          undefined,
          undefined,
          reportId,
        );
      });
      const notificationResults = await Promise.all(notificationPromises);
      portfolionotificaiton = notificationResults.flatMap(result =>
        result.data.allNotificationDeliveries.edges.map(
          (e: any) => e.node.sesMessageId,
        ),
      );
    } else {
      portfolionotificaiton = allNotificationDeliveriesList.data?.allNotificationDeliveries
        .filter(n => reportIds.includes(n.reportId))
        .map((e: any) => e.sesMessageId);
    }
    setPortfolioNotificationIds([...portfolionotificaiton.filter(Boolean)]);
  };

  const findNotificationsForNPOIds = async (
    client: ApolloClient<object>,
    npoIdsArray: number[],
  ) => {
    setLoading(true);
    const reportPromises = Array.from(npoIdsArray).map(
      async (npoId: number) => {
        return getReports(client!, undefined, npoId);
      },
    );
    const reportResults = await Promise.all(reportPromises);
    const reportIds = reportResults.flatMap(result =>
      result.data.allReports.edges.map((e: any) => e.node.reportId),
    );
    let NPONotificaiton: any = [];
    if (!allNotificationDeliveriesList.data) {
      const notificationPromises = reportIds.map(async (reportId: number) => {
        return getNotificationDeliveriesByFilters(
          client!,
          undefined,
          undefined,
          undefined,
          undefined,
          reportId,
        );
      });
      const notificationResults = await Promise.all(notificationPromises);
      NPONotificaiton = notificationResults.flatMap(result =>
        result.data.allNotificationDeliveries.edges.map(
          (e: any) => e.node.sesMessageId,
        ),
      );
      setNPONotificationIds([...NPONotificaiton.filter(Boolean)]);
    } else {
      NPONotificaiton = allNotificationDeliveriesList.data?.allNotificationDeliveries
        .filter(n => reportIds.includes(n.reportId))
        .map((e: any) => e.sesMessageId);
    }
    setNPONotificationIds([...NPONotificaiton.filter(Boolean)]);
  };

  const findNotificationsForDonorIds = async (
    client: ApolloClient<object>,
    donorIdsArray: number[],
  ) => {
    setLoading(true);
    let donorNotifications: any = [];
    if (!allNotificationDeliveriesList.data) {
      const reportPromises = Array.from(donorIdsArray).map(
        async (donorId: number) => {
          return getNotificationDeliveriesByFilters(
            client!,
            undefined,
            undefined,
            donorId,
          );
        },
      );
      const notificationResults = await Promise.all(reportPromises);
      donorNotifications = notificationResults.flatMap(result =>
        result.data.allNotificationDeliveries.edges.map(
          (e: any) => e.node.sesMessageId,
        ),
      );
    } else {
      donorNotifications = allNotificationDeliveriesList.data?.allNotificationDeliveries
        .filter(n => donorIdsArray.includes(n.accountId))
        .map((e: any) => e.sesMessageId);
    }
    setDonorNotificationIds([...donorNotifications.filter(Boolean)]);
  };

  const findNotificationsForInvestmentId = async (
    client: ApolloClient<object>,
    investmentId: number,
  ) => {
    setLoading(true);
    let investmentNotifications: any = [];
    if (!allNotificationDeliveriesList.data) {
      const reportPromises = Array.from([investmentId]).map(
        async (investmentId: number) => {
          return getNotificationDeliveriesByFilters(
            client!,
            undefined,
            undefined,
            undefined,
            investmentId,
          );
        },
      );
      const notificationResults = await Promise.all(reportPromises);
      investmentNotifications = notificationResults.flatMap(result =>
        result.data.allNotificationDeliveries.edges.map(
          (e: any) => e.node.sesMessageId,
        ),
      );
    } else {
      investmentNotifications = allNotificationDeliveriesList.data?.allNotificationDeliveries
        .filter(n => n.investmentId === investmentId)
        .map((e: any) => e.sesMessageId);
    }
    setInvestmentNotificationIds([...investmentNotifications.filter(Boolean)]);
  };

  useEffect(() => {
    setCount(logs.messageIds.length);
  }, [logs]);

  useEffect(() => {
    if (count) {
      const newTotalPages = Math.ceil((logs.messageIds.length || 0) / PER_PAGE);
      setTotalPages(newTotalPages);
      setCurrentPage(1);
    }
  }, [count, logs]);

  const handlePageChange = (page: React.SetStateAction<number>) => {
    setCurrentPage(page);
  };
  const pages = [];
  for (
    let i = Math.max(1, currentPage - 5);
    i <= Math.min(totalPages, currentPage + 5);
    i++
  ) {
    pages.push(i);
  }

  // NPO list for dropdown
  useEffect(() => {
    makeAccountsListRequest(getAccounts(client!));
  }, [setAccountsList]);
  // Portfolio list for dropdown
  useEffect(() => {
    makePortfoliosListRequest(getPortfolios(client!));
  }, [setPortfoliosList]);
  // Program list for dropdown
  useEffect(() => {
    makeProgramsListRequest(getPrograms(client!));
  }, [setProgramsList]);
  // Donor list for dropdown
  useEffect(() => {
    makeDonorsListRequest(getDonorsFilters(client!));
  }, [setDonorsList]);
  // Deferred: fetch all deliveries only once a delivery-based filter is used.
  useEffect(() => {
    const usesDeliveryFilter =
      selectedPrograms.length ||
      selectedPortfolios.length ||
      selectedNPO.length ||
      selectedDonors.length ||
      selectedInvestment;
    if (
      usesDeliveryFilter &&
      !allNotificationDeliveriesList.data &&
      allNotificationDeliveriesList.type !== 'REQUEST_START'
    ) {
      makeAllNotificationDeliveriesListRequest(
        getAllNotificationDeliveries(client!),
      );
    }
  }, [
    selectedPrograms,
    selectedPortfolios,
    selectedNPO,
    selectedDonors,
    selectedInvestment,
  ]);

  useEffect(() => {
    if (investmentIdInput) {
      setSelectedInvestment(parseInt(investmentIdInput));
    } else {
      setSelectedInvestment(undefined);
    }
  }, [investmentIdInput]);

  const handleClearAllFilters = () => {
    setSearchEmail('');
    setDate(null);
    setType(undefined);
    setSelectedPrograms([]);
    setProgramNotificationIds([]);
    setSelectedPortfolios([]);
    setPortfolioNotificationIds([]);
    setSelectedNPO([]);
    setNPONotificationIds([]);
    setSelectedDonors([]);
    setDonorNotificationIds([]);
    setSelectedInvestment(undefined);
    setInvestmentNotificationIds([]);
    setRecencyDate(null);
    setInvestmentIdInput('');
  };

  return (
    <StyledDiv>
      <div className="left-grid">
        <EmailSearchField
          handleSearchEmail={setSearchEmail}
          className="search-field"
        />
        <DateSelector
          date={date}
          handleDate={setDate}
          optionsMap={MONTHS}
          recencyDate={recencyDate}
          className="date-selector"
        />
        <MessageType
          type={type}
          handleType={setType}
          className="type-selector"
        />
        <div style={{ marginTop: '20px' }} />
        <FilterSelector
          filters={accountsList.data?.adminAccounts || []}
          handleFilter={setSelectedNPO}
          className="npo-selector"
          label={'NPO'}
          property="dbId"
          selected={selectedNPO}
        />
        <div style={{ marginTop: '20px' }} />
        <FilterSelector
          filters={
            donorsList.data?.adminAccounts.filter(donor => donor.name) || []
          }
          handleFilter={setSelectedDonors}
          className="donor-selector"
          label={'Donor'}
          property="dbId"
          selected={selectedDonors}
        />
        <div />
      </div>
      <div className="right-grid">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            color: COLORS.whiteBase,
            backgroundColor: COLORS.greyDark,
            padding: SPACERS.small,
          }}
        >
          <div className="heading-label" style={{ color: COLORS.whiteBase }}>
            Filters Applied:
          </div>
          <div
            style={{
              gap: SPACERS.tiny,
              padding: anyFiltersApplied ? SPACERS.small : 0,
            }}
          >
            {date && <StyledDiv4 />}
            {date && (
              <StyledDiv3>
                <strong>Date:</strong> {date?.toISOString().split('T')[0]}{' '}
                {/*For spacing*/}
                <FontAwesomeIcon
                  style={{ cursor: 'pointer', color: COLORS.critical }}
                  icon={faTimes}
                  onClick={() => {
                    setDate(undefined);
                  }}
                ></FontAwesomeIcon>
              </StyledDiv3>
            )}
            {/* Recency filter hidden - restore block below to re-enable
            {recencyDate && <StyledDiv4 />}
            {recencyDate && (
              <StyledDiv3>
                <strong>Recency:</strong>{' '}
                {`From ${
                  recencyDate?.toISOString().split('T')[0]
                } until today `}
                <FontAwesomeIcon
                  style={{ cursor: 'pointer', color: COLORS.critical }}
                  icon={faTimes}
                  onClick={() => {
                    setRecencyDate(null);
                  }}
                ></FontAwesomeIcon>
              </StyledDiv3>
            )}
            */}
            {type && <StyledDiv4 />}
            {type && (
              <StyledDiv3>
                <strong>Type:</strong> {type}{' '}
                <FontAwesomeIcon
                  style={{ cursor: 'pointer', color: COLORS.critical }}
                  icon={faTimes}
                  onClick={() => {
                    setType(undefined);
                  }}
                ></FontAwesomeIcon>
              </StyledDiv3>
            )}
            {selectedPrograms.length > 0 && <StyledDiv4 />}
            <FilterPill
              filters={programsList.data?.allPrograms || []}
              selected={selectedPrograms}
              filterType="Program"
              changeSelected={setSelectedPrograms}
            />
            {selectedPortfolios.length > 0 && <StyledDiv4 />}
            <FilterPill
              filters={portfoliosList.data?.adminPortfolios || []}
              selected={selectedPortfolios}
              filterType="Portfolio"
              changeSelected={setSelectedPortfolios}
            />
            {selectedNPO.length > 0 && <StyledDiv4 />}
            <FilterPill
              filters={accountsList.data?.adminAccounts || []}
              selected={selectedNPO}
              filterType="NPO"
              changeSelected={setSelectedNPO}
            />
            {selectedDonors.length > 0 && <StyledDiv4 />}
            <FilterPill
              filters={donorsList.data?.adminAccounts || []}
              selected={selectedDonors}
              filterType="Donor"
              changeSelected={setSelectedDonors}
            />
            {selectedInvestment && <StyledDiv4 />}
            {selectedInvestment && (
              <StyledDiv3>
                <strong>Investment ID:</strong>
                <span style={{ margin: '5px' }}>{selectedInvestment}</span>
                <Tooltip title="Remove Filter">
                  <FontAwesomeIcon
                    style={{ cursor: 'pointer', color: COLORS.critical }}
                    icon={faTimes}
                    onClick={() => setInvestmentIdInput('')}
                  ></FontAwesomeIcon>
                </Tooltip>
              </StyledDiv3>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingTop: anyFiltersApplied ? SPACERS.medium : SPACERS.tiny,
              }}
            >
              <Checkbox
                onChange={() => setLatest(!latest)}
                style={{ color: COLORS.whiteBase, marginLeft: '-9px' }}
              />
              Sort by Most Recent
            </span>
            <ClearButton
              onClick={handleClearAllFilters}
              className="clear-filters"
              disabled={!anyFiltersApplied}
            >
              Clear All Filters
            </ClearButton>
          </div>
        </div>
        <LogsCountWrapper>
          <TrueImpactText variant="heading-subheading">
            {anyFiltersApplied
              ? loading
                ? ''
                : `Email Logs Found: ${count}`
              : 'Please enter an email address or filters above to retrieve events.'}
          </TrueImpactText>
        </LogsCountWrapper>

        {loading && <TrueImpactLoading />}
        {(latest ? logs.messageIds.toReversed() : logs.messageIds)
          .slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)
          .map(id => (
            <div>
              <LogCard key={id} {...logs.events[id]} />
            </div>
          ))}
        {!loading && count > 0 && (
          <div>
          <PaginationContainer>
            {currentPage > 1 && (
              <>
                <PaginationButton onClick={() => handlePageChange(1)}>
                  first
                </PaginationButton>
                <PaginationButton
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {'<'}
                </PaginationButton>
              </>
            )}
            {pages.map(page => (
              <PaginationButton
                key={page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationButton>
            ))}
            {currentPage < totalPages && (
              <>
                <PaginationButton
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {'>'}
                </PaginationButton>
                <PaginationButton onClick={() => handlePageChange(totalPages)}>
                  last
                </PaginationButton>
              </>
            )}
          </PaginationContainer>
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export default SESLogs;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
`;
const PaginationButton = styled.button`
  color: #0073a3;
  margin: 0 5px;
  border: none;
  background: none;
  cursor: pointer;
`;

const StyledDiv4 = styled.div`
  border: none;
  height: 1px;
  background-color: ${COLORS.greyBase};
  margin: ${SPACERS.tiny};
`;

const LogsCountWrapper = styled.div`
  margin: ${SPACERS.small} 0;
`;

const ClearButton = styled.button`
  diplay: flex;
  align-items: center;
  padding-left: 24px;
  padding-right: 24px;
  height: 40px;
  color: ${COLORS.whiteBase};
  border-color: ${COLORS.whiteBase};
  border-radius: 4px;
  background-color: inherit;
  cursor: pointer;
  margin-right: 32px;
  margin-top: 26px;
  &[disabled] {
    visibility: hidden;
  }
`;

export const investmentById = gql`
  query allInvestments($accountId: Int!) {
    allInvestments(filters: { accountId: $accountId }) {
      edges {
        node {
          dbId
        }
      }
    }
  }
`;

interface FilterPillProps {
  filters: any[];
  filterType: string;
  selected: number[];
  changeSelected: React.Dispatch<React.SetStateAction<number[]>>;
  property?: string;
}

const FilterPill: React.FC<FilterPillProps> = ({
  filters,
  selected,
  filterType,
  changeSelected,
  // onRemove,
  // property,
}) => {
  let property = 'dbId';
  if (filterType === 'Program') property = 'Id';
  return (
    <div style={{ gap: '10px', display: 'flex', flexWrap: 'wrap' }}>
      {Array.from(selected).map(id => {
        const filter = filters?.find(p => p[property] === id);
        return filter ? (
          <StyledDiv3>
            <strong>{filterType}:</strong>
            <span style={{ margin: '5px', whiteSpace: 'nowrap' }}>
              {filter.name}
            </span>
            <Tooltip title="Remove Filter">
              <FontAwesomeIcon
                style={{ cursor: 'pointer', color: COLORS.critical }}
                icon={faTimes}
                onClick={() => {
                  if (selected.includes(id)) {
                    changeSelected(prev => prev.filter(d => d !== id));
                  }
                }}
              ></FontAwesomeIcon>
            </Tooltip>
          </StyledDiv3>
        ) : null;
      })}
    </div>
  );
};

const StyledDiv3 = styled.div`
  color: ${COLORS.blackBase};
  background-color: ${COLORS.whiteBase};
  padding: ${SPACERS.tiny};
  border-radius: 50px;
  width: fit-content;
  gap: ${SPACERS.tiny};
`;
